// Contractor invite step 3 — what the work actually is. RON-5466 Phase 4.
// Prototype: `CStep3`, the card titled "Nature of work".
//
// CONTRACTOR ONLY. The visitor wizard has no equivalent and must not grow one — a visitor is a
// guest, a contractor is somebody doing a job on the premises, and this card is the difference.
// Nothing here is shared: `VisitDetailsStep` (step 2) is used by both wizards and is deliberately
// untouched.
//
// WHY THIS ARRIVED LATE. The flow shipped with three steps against the prototype's four, and the
// module's own documentation described that as "three, not the visitor wizard's four" — a
// comparison with the wrong wizard, which read like a decision and hid the omission. It was found
// on 10-October-2026 by writing `contractorInviteParityMap.js`, not by looking at the screen.
//
// ── Three of these five fields have somewhere to go; two do not ─────────────────────────────
//
//   work_description  ✅ RON-6002 §6 lists it as a contractor-only create field
//   areas             ✅ same
//   personnel         ✅ same
//   duration          ❌ no API field exists
//   supervisor        ❌ no API field exists
//
// The last two are built to the prototype's design and **are not sent anywhere**. That is a
// deliberate order of work: build the screen, then ask for the fields. Until they exist, what a
// host types into them is kept for the length of the wizard, shown back on the review step, and
// then dropped on submit.
//
// That is a real cost and it is written down rather than hidden: a supervisor's name typed here
// does not reach reception. `ContractorInviteWizard` has a test asserting these two are NOT in
// the payload, so wiring them later is a deliberate change rather than something that happens by
// accident the day a field appears.

import { useTranslation } from "react-i18next";

import VisitorFormRow, { fieldClassName } from "./VisitorFormRow.jsx";

// The prototype's `CTYPES`, and the server's codes — they agree exactly as of
// answers-to-frontend-8 (14-October-2026). Value keys rather than display text, so the stored
// value does not change when the wording does.
//
// CORRECTED 16-October-2026. This list had "ongoing" and no "under_1h". Neither was ours to
// decide: `CTYPES` in `web-visitor-manager.html` is
// ['Less than 1 hour','Half day','Full day','Multiple days'] and there is no "Ongoing" in it.
// Caught by the Laravel team reading our note against the prototype, not by us.
//
// Anything outside this list is a 422 on their side, so an invented value would not have failed
// quietly — it would have failed at submit, on the last step of the wizard.
export const WORK_DURATIONS = [
  "under_1h",
  "half_day",
  "full_day",
  "multiple_days",
];

export const DURATION_LABEL_KEYS = {
  under_1h: "react_visitors_duration_under_1h",
  half_day: "react_visitors_duration_half_day",
  full_day: "react_visitors_duration_full_day",
  multiple_days: "react_visitors_duration_multiple_days",
};

// The prototype pre-selects by the dates already chosen: a booking that spans days is "Multiple
// days", anything else "Full day". Exported so the wizard can seed the form without repeating it.
export const defaultDuration = (startDate, endDate) =>
  startDate && endDate && startDate !== endDate ? "multiple_days" : "full_day";

const ContractorWorkStep = ({ form, errors, onField }) => {
  const { t } = useTranslation("common");

  return (
    <div className="visitor-card">
      <p className="visitor-card__title">{t("react_visitors_work_title")}</p>

      <VisitorFormRow
        id="contractor-work-description"
        label={t("react_visitors_field_work_description")}
        error={errors?.workDescription ? t(errors.workDescription) : null}
      >
        {/* A textarea, as the prototype has it. The work is a sentence, not a line — "replacement
            of the 3rd-floor distribution board, isolating the riser" does not fit an input, and
            truncating it is how reception ends up guessing. */}
        <textarea
          id="contractor-work-description"
          className={fieldClassName(form.workDescription, errors?.workDescription, false)}
          rows={3}
          value={form.workDescription}
          onChange={(event) => onField("workDescription", event.target.value)}
          placeholder={t("react_visitors_work_description_placeholder")}
        />
      </VisitorFormRow>

      <VisitorFormRow
        id="contractor-work-areas"
        label={t("react_visitors_field_work_areas")}
        hint={t("react_visitors_work_areas_hint")}
        error={errors?.areas ? t(errors.areas) : null}
      >
        <input
          id="contractor-work-areas"
          type="text"
          className={fieldClassName(form.areas, errors?.areas)}
          value={form.areas}
          onChange={(event) => onField("areas", event.target.value)}
          placeholder={t("react_visitors_work_areas_placeholder")}
        />
      </VisitorFormRow>

      <div className="visitor-field-grid">
        <VisitorFormRow
          id="contractor-work-duration"
          label={t("react_visitors_field_work_duration")}
        >
          <select
            id="contractor-work-duration"
            className={fieldClassName(form.duration, null, false)}
            value={form.duration}
            onChange={(event) => onField("duration", event.target.value)}
          >
            {WORK_DURATIONS.map((value) => (
              <option key={value} value={value}>
                {t(DURATION_LABEL_KEYS[value])}
              </option>
            ))}
          </select>
        </VisitorFormRow>

        <VisitorFormRow
          id="contractor-work-personnel"
          label={t("react_visitors_field_work_personnel")}
        >
          <input
            id="contractor-work-personnel"
            type="number"
            min={1}
            className={fieldClassName(form.personnel, null, false)}
            value={form.personnel}
            // Held as a string in form state and coerced once, on submit. Coercing on every
            // keystroke makes the field impossible to clear — `Number("")` is 0, which reappears
            // in the box the moment somebody deletes the digit to type a different one.
            onChange={(event) => onField("personnel", event.target.value)}
          />
        </VisitorFormRow>
      </div>

      <VisitorFormRow
        id="contractor-work-supervisor"
        label={t("react_visitors_field_work_supervisor")}
      >
        <input
          id="contractor-work-supervisor"
          type="text"
          className={fieldClassName(form.supervisor, null)}
          value={form.supervisor}
          onChange={(event) => onField("supervisor", event.target.value)}
          placeholder={t("react_visitors_work_supervisor_placeholder")}
        />
      </VisitorFormRow>
    </div>
  );
};

export default ContractorWorkStep;
